'use client';

import { useEffect, useRef, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShopperSearch } from '@/scapi';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import EatalyPlpDateFilter from '@/components/eataly-plp-date-filter';
import EatalyPlpPriceFilter from '@/components/eataly-plp-price-filter';
import EatalyPlpVenueFilter from '@/components/eataly-plp-venue-filter';
import EatalyPlpOrdinationOptions from '@/components/eataly-plp-ordination/plp-ordination-options';
import { usePlpOrdinationSelect } from '@/components/eataly-plp-ordination/use-plp-ordination-select';
import { getExcludedRefinementAttributeIds } from '@/components/eataly-plp-filters/lib/promoted-refinements';
import type { SiblingFolderLink } from '@/lib/api/content-route-index.server';
import {
    getListingResultsCountAriaLabel,
    type ListingResultsCountTranslateFn,
} from '@/lib/listing-results-count-aria-label';
import { usePlpRefinements } from '../eataly-plp-filters/hooks/use-plp-refinements';
import EatalyPlpFiltersDrawerWithTrigger from '@/components/eataly-plp-filters/plp-filters-drawer-with-trigger';
import { useMediaQuery } from '@/hooks/use-media-query';
import { cn } from '@/lib/utils';
import { SvgClose } from '../icons-eataly';

const LG_UP = '(min-width: 1024px)';

export type PlpSortingOption = {
    id: string;
    label: string;
};

type PlpStickyBarResult = {
    total?: number;
    sortingOptions?: ShopperSearch.schemas['ProductSearchResult']['sortingOptions'];
    refinements?: ShopperSearch.schemas['ProductSearchResult']['refinements'];
    selectedRefinements?: ShopperSearch.schemas['ProductSearchResult']['selectedRefinements'];
};

interface EatalyCategoryPlpStickyBarProps {
    result: PlpStickyBarResult;
    refine?: string[];
    filtersOpen?: boolean;
    disableFilters?: boolean;
    onFiltersDrawerOpenChange?: (open: boolean) => void;
    ordinationOpen: boolean;
    onToggleOrdination: () => void;
    onOrdinationOpenChange: (open: boolean) => void;
    /** Content folder listings: sort ids from config; labels come from i18n. */
    sortingOptionIds?: string[];
    /** Sort selected when the URL has no `sort` param. News listings use newest-first. */
    defaultSortId?: string;
    /** i18n key for the listing count label. Defaults to product PLP copy. */
    countLabelKey?: 'category:banner.productsAvailable' | 'categoryPlp:contentFolderListing.itemsAvailable';
    /** Promote Prezzo onto the sticky bar (events/courses listings only). */
    promotePriceFilter?: boolean;
    /**
     * True while listing data is reloading (e.g. category→category PLP navigation).
     * Shows the counting label so the previous PLP total does not stay stale.
     */
    isListingDataPending?: boolean;
    paginationLimit: number;
    pageNumber: number;
    /** Route category id used to list sibling categories in Filtra. */
    categoryId?: string;
    /** Category custom attribute `c_showCategoryFilter`. */
    showCategoryFilter?: boolean;
    /** Attribute ids hidden from Filtra and excluded from the filter badge. */
    excludeRefinementAttributeIds?: readonly string[];
    /**
     * When set, decides whether Filtra is shown without looking at search refinements.
     * Editorial listings use this for child-folder links.
     */
    filtersAvailable?: boolean;
    /** Editorial listings: child folders shown in Filtra instead of search refinements. */
    folderLinks?: SiblingFolderLink[];
}

function SortTriggerContent({
    sortLabel,
    hasActiveSort,
    selectedSortingOptionLabel,
}: {
    sortLabel: string;
    hasActiveSort: boolean;
    selectedSortingOptionLabel: string;
}): ReactElement {
    return (
        <>
            <span>{sortLabel}</span>
            {hasActiveSort ? <span className="body-copy-m hidden md:inline">{selectedSortingOptionLabel}</span> : null}
        </>
    );
}

export default function EatalyCategoryPlpStickyBar({
    result,
    refine = [],
    filtersOpen = false,
    disableFilters = false,
    onFiltersDrawerOpenChange,
    ordinationOpen,
    onToggleOrdination,
    onOrdinationOpenChange,
    sortingOptionIds,
    defaultSortId,
    countLabelKey = 'category:banner.productsAvailable',
    promotePriceFilter = false,
    isListingDataPending = false,
    paginationLimit,
    pageNumber,
    categoryId,
    showCategoryFilter = false,
    excludeRefinementAttributeIds = [],
    filtersAvailable,
    folderLinks,
}: EatalyCategoryPlpStickyBarProps): ReactElement {
    const { t } = useTranslation(['category', 'categoryPlp', 'categoryRefinements']);
    const isLgUp = useMediaQuery(LG_UP);
    const {
        effectiveSortingOption,
        selectedSortingOptionLabel,
        isPending: isOrdinationPending,
        sortingOptions,
        selectSort,
    } = usePlpOrdinationSelect(result, { sortingOptionIds, defaultSortId });
    const {
        hasUsableRefinements,
        selectedFiltersCount,
        isPending: isCountPending,
    } = usePlpRefinements({
        result,
        refine,
        disabled: disableFilters,
        excludeRefinementAttributeIds: [
            ...getExcludedRefinementAttributeIds({ promotePrice: promotePriceFilter }),
            ...excludeRefinementAttributeIds,
        ],
        categoryId,
        showCategoryFilter,
    });

    const hasSortOptions = sortingOptions.length > 0;
    const ordinationTitle = t('categoryPlp:ordinationDrawer.title');

    // The mobile ordination drawer is rendered by the page (no <DrawerTrigger>): when it closes and focus
    // dropped to <body>, hand it back to the sort trigger.
    const sortTriggerRef = useRef<HTMLButtonElement>(null);

    // This bar sticks below the header, so keyboard focus can also end up hidden behind it: publish its height
    // for the document `scroll-padding-top` (see style.css).
    const stickyBarRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const bar = stickyBarRef.current;
        if (!bar) {
            return;
        }
        const root = document.documentElement;
        const publishHeight = () => root.style.setProperty('--plp-sticky-bar-height', `${bar.offsetHeight}px`);
        publishHeight();
        const observer = new ResizeObserver(publishHeight);
        observer.observe(bar);
        return () => {
            observer.disconnect();
            root.style.removeProperty('--plp-sticky-bar-height');
        };
    }, []);
    const wasOrdinationOpenRef = useRef(false);
    useEffect(() => {
        if (wasOrdinationOpenRef.current && !ordinationOpen) {
            requestAnimationFrame(() => {
                if (document.activeElement === document.body) {
                    sortTriggerRef.current?.focus();
                }
            });
        }
        wasOrdinationOpenRef.current = ordinationOpen;
    }, [ordinationOpen]);

    const isCountLoading = isCountPending || isListingDataPending;
    const displayCount = result.total ?? 0;

    const countLabel = isCountLoading
        ? t('category:banner.counting')
        : countLabelKey === 'categoryPlp:contentFolderListing.itemsAvailable'
          ? t('categoryPlp:contentFolderListing.itemsAvailable', { count: displayCount })
          : t('category:banner.productsAvailable', { count: displayCount });

    const resultsCountAriaLabel = isCountLoading
        ? countLabel
        : getListingResultsCountAriaLabel(t as ListingResultsCountTranslateFn, {
              kind: countLabelKey === 'categoryPlp:contentFolderListing.itemsAvailable' ? 'items' : 'products',
              total: displayCount,
              pageNumber,
              paginationLimit,
          });

    const sortLabel = t('categoryPlp:stickyBar.sortLabel');
    const hasActiveSort = Boolean(effectiveSortingOption);

    const sortButtonAriaLabel = hasActiveSort
        ? t('categoryPlp:stickyBar.sortButtonLabelWithSelection', {
              label: sortLabel,
              selection: selectedSortingOptionLabel,
          })
        : sortLabel;

    const filterLabel = t('categoryPlp:stickyBar.filterLabel');
    const filterButtonAriaLabel =
        selectedFiltersCount > 0
            ? t('categoryRefinements:filtersButtonLabelWithCount', {
                  label: filterLabel,
                  count: selectedFiltersCount,
              })
            : filterLabel;

    const sortButtonClassName = 'h-10 min-h-10 gap-1 px-4 py-2 cursor-pointer focus-visible:ring-ring';

    const sortTriggerContent = (
        <SortTriggerContent
            sortLabel={sortLabel}
            hasActiveSort={hasActiveSort}
            selectedSortingOptionLabel={selectedSortingOptionLabel}
        />
    );

    const showFilters = !disableFilters && (filtersAvailable ?? (selectedFiltersCount > 0 || hasUsableRefinements));

    return (
        <div
            ref={stickyBarRef}
            data-testid="plp-sticky-bar"
            className="sticky z-40 w-full border-b border-sand-95 bg-neutral-white transition-[top] duration-(--header-transition-duration,0ms) ease-out motion-reduce:transition-none"
            style={{
                top: 'var(--header-visible-offset, 0px)',
            }}>
            <div className="grid-global py-3">
                {/* Below lg: flex row that wraps the count under the buttons when space runs out. At lg+ `contents` lets the children sit on the global grid. */}
                <div className="col-span-full flex flex-wrap-reverse items-center justify-between gap-x-4 gap-y-2 lg:contents">
                    <p
                        className="body-copy-m flex items-center text-neutral-10 lg:col-start-2 lg:col-span-2"
                        aria-live="polite">
                        <span aria-hidden="true">{countLabel}</span>
                        <span className="sr-only">{resultsCountAriaLabel}</span>
                    </p>

                    <div className="flex items-center justify-end gap-2 lg:col-start-10 lg:col-end-12">
                        {hasSortOptions &&
                            (isLgUp ? (
                                <Popover modal open={ordinationOpen} onOpenChange={onOrdinationOpenChange}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            ref={sortTriggerRef}
                                            data-testid="plp-sticky-bar-sort-trigger"
                                            hierarchy="primary"
                                            mode="dark"
                                            size="small"
                                            className={sortButtonClassName}
                                            aria-haspopup="dialog"
                                            // Modal dialog: Radix adds aria-expanded to its trigger, which is not appropriate here.
                                            aria-expanded={undefined}
                                            aria-label={sortButtonAriaLabel}>
                                            {sortTriggerContent}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        data-testid="plp-ordination-popover"
                                        side="bottom"
                                        align="end"
                                        sideOffset={8}
                                        className={cn(
                                            'w-auto min-w-[240px] rounded border border-sand-95 bg-neutral-white p-4 shadow-md flex flex-col gap-4',
                                            'text-neutral-10'
                                        )}
                                        aria-label={ordinationTitle}>
                                        <div className="flex  items-center justify-between">
                                            <h2 className="heading-title-m">{ordinationTitle}</h2>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon-lg"
                                                onClick={() => onOrdinationOpenChange(false)}
                                                className="shrink-0 rounded-full border border-transparent bg-neutral-white hover:border-neutral-80"
                                                aria-label={t('categoryPlp:ordinationDrawer.closeAriaLabel')}>
                                                <SvgClose className=" size-6 text-neutral-10" aria-hidden />
                                            </Button>
                                        </div>
                                        <EatalyPlpOrdinationOptions
                                            sortingOptions={sortingOptions}
                                            effectiveSortingOption={effectiveSortingOption}
                                            isPending={isOrdinationPending}
                                            onSelectSort={selectSort}
                                            ariaLabel={ordinationTitle}
                                            idPrefix="ordination-popover"
                                        />
                                    </PopoverContent>
                                </Popover>
                            ) : (
                                <Button
                                    ref={sortTriggerRef}
                                    data-testid="plp-sticky-bar-sort-trigger"
                                    hierarchy="primary"
                                    mode="dark"
                                    size="small"
                                    className={sortButtonClassName}
                                    aria-haspopup="dialog"
                                    aria-label={sortButtonAriaLabel}
                                    onClick={onToggleOrdination}>
                                    {sortTriggerContent}
                                </Button>
                            ))}

                        {!disableFilters && (
                            <>
                                <EatalyPlpVenueFilter
                                    refinements={result.refinements}
                                    refine={refine}
                                    triggerClassName={sortButtonClassName}
                                    testIdPrefix="plp"
                                />
                                <EatalyPlpPriceFilter
                                    enabled={promotePriceFilter}
                                    refinements={result.refinements}
                                    refine={refine}
                                    triggerClassName={sortButtonClassName}
                                    testIdPrefix="plp"
                                />
                                <EatalyPlpDateFilter
                                    refinements={result.refinements}
                                    refine={refine}
                                    triggerClassName={sortButtonClassName}
                                    testIdPrefix="plp"
                                />
                            </>
                        )}

                        {showFilters && onFiltersDrawerOpenChange ? (
                            <EatalyPlpFiltersDrawerWithTrigger
                                open={filtersOpen}
                                onOpenChange={onFiltersDrawerOpenChange}
                                triggerTestId="plp-sticky-bar-filter-trigger"
                                triggerClassName={sortButtonClassName}
                                filterButtonAriaLabel={filterButtonAriaLabel}
                                filterLabel={filterLabel}
                                selectedFiltersCount={selectedFiltersCount}
                                result={result as ShopperSearch.schemas['ProductSearchResult']}
                                refine={refine}
                                excludeRefinementAttributeIds={getExcludedRefinementAttributeIds({
                                    promotePrice: promotePriceFilter,
                                })}
                                categoryId={categoryId}
                                showCategoryFilter={showCategoryFilter}
                                folderLinks={folderLinks}
                                countLabelKey={countLabelKey}
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}
